import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { crmCampaignsAPI } from '../../services/api';
import {
  DeleteDialog, EditableTable, RecordFormDialog, SingleGamesSection,
  MAX_FREESPINS, MAX_WAGERING, isFreespinCount, isValidAmount, isValidCurrency, useEditableList,
} from './campaignShared';

// Friday Loyalty Bonus: its one games row, then max amounts per currency, loss tiers and values per
// tag. The limits table is keyed by A/B test group and currency, but the campaign uses one of
// each (A, USD); the backend fills them in and the page only names the currency. Every rule here
// is the backend's too; these checks are hints.

// Shown for an empty value: no freespins.
const NONE = '/';
const chars = (s) => [...s.trim()].length;

// --- limits ---

// Loss tiers: each is edited as its min loss alone, and the backend derives every upper end from
// the next tier's min, as with Second Chance. The deposit threshold is one value for the whole
// campaign (stored on every row), shown and edited above the table; only the first tier brings
// its own. Freespins come with a count, cashback without.
const BONUS_TYPES = [
  { value: 'freespins', label: 'Freespins' },
  { value: 'cashback', label: 'Cashback' },
];
const isCashback = (v) => v.bonus_type === 'cashback';

const LIMIT_FIELDS = [
  { key: 'loss_amount_from', label: 'Min loss', inputMode: 'decimal' },
  { key: 'bonus_type', label: 'Bonus type', options: BONUS_TYPES, initial: 'freespins' },
  { key: 'freespin_number', label: 'Freespin count', inputMode: 'numeric', placeholder: NONE, disabled: isCashback },
];
// With no tiers yet there is no stored threshold, so the first one sets it.
const FIRST_LIMIT_FIELDS = [
  { key: 'deposit_amount_from', label: 'Min deposit', inputMode: 'decimal' },
  ...LIMIT_FIELDS,
];

const validateLimit = (v) => {
  if (!isValidAmount(v.loss_amount_from || '')) return 'Min loss must be a non-negative number.';
  if ('deposit_amount_from' in v && !isValidAmount(v.deposit_amount_from || '')) return 'Min deposit must be a non-negative number.';
  if (v.bonus_type === 'freespins' && !isFreespinCount(v.freespin_number || '')) return `Freespins need a freespin count: a whole number from 1 to ${MAX_FREESPINS}.`;
  return '';
};

const submitLimit = (v, initial) => {
  const body = {
    loss_amount_from: v.loss_amount_from.trim(),
    bonus_type: v.bonus_type,
    freespin_number: isCashback(v) ? null : Number(v.freespin_number),
  };
  if (initial) return crmCampaignsAPI.updateFridayLoyaltyLimit(initial.loss_amount_from, body);
  if ('deposit_amount_from' in v) body.deposit_amount_from = v.deposit_amount_from.trim();
  return crmCampaignsAPI.createFridayLoyaltyLimit(body);
};

// Listed by min loss (the backend's order).
const LIMIT_COLUMNS = [
  { label: 'Min loss', className: 'text-right', render: (r) => r.loss_amount_from },
  { label: 'Bonus type', render: (r) => BONUS_TYPES.find((b) => b.value === r.bonus_type)?.label ?? r.bonus_type },
  { label: 'Freespin count', className: 'text-right', render: (r) => r.freespin_number ?? NONE },
];

const DEPOSIT_FIELDS = [{ key: 'deposit_amount_from', label: 'Min deposit', inputMode: 'decimal' }];
const validateDeposit = (v) => (isValidAmount(v.deposit_amount_from || '') ? '' : 'Min deposit must be a non-negative number.');
const submitDeposit = (v) => crmCampaignsAPI.setFridayLoyaltyDeposit({ deposit_amount_from: v.deposit_amount_from.trim() });

function LimitsSection() {
  const list = useEditableList(crmCampaignsAPI.listFridayLoyaltyLimits, 'limits');
  const [editingDeposit, setEditingDeposit] = useState(false);
  const currency = list.data?.currency || 'USD';
  const deposit = list.data?.deposit_amount_from;
  // Stable while open: the dialog resets its values whenever `initial` changes.
  const depositRow = useMemo(() => ({ deposit_amount_from: deposit }), [deposit]);
  const d = list.deleting;
  const description = (
    <>
      Bonus a player gets, based on their losses. A tier applies from its min loss up to the next tier&apos;s.
      {deposit != null && (
        <>
          {' '}Players must have deposited at least <span className="font-mono text-ink">{deposit} {currency}</span>.
          <Button variant="link" size="sm" className="h-auto p-0 pl-1.5 text-xs" onClick={() => setEditingDeposit(true)}>Edit</Button>
        </>
      )}
      {list.data?.irregular && ' The stored rows have differing deposit amounts or loss gaps; the next save closes them.'}
    </>
  );
  return (
    <>
      <EditableTable
        defaultCollapsed title={`Loss tiers (${currency})`} description={description}
        addLabel="Add tier" emptyText="No loss tiers yet."
        loading={list.loading} error={list.error} rows={list.rows} columns={LIMIT_COLUMNS}
        rowKey={(r) => r.loss_amount_from}
        onAdd={list.openAdd} onEdit={list.openEdit} onDelete={list.setDeleting}
      />
      <RecordFormDialog
        open={list.form.open} initial={list.form.row} title="loss tier" submitLabel="Add tier"
        description={`Amounts are in ${currency}. A tier applies from its min loss up to the next tier's; the highest tier has no upper limit.${deposit == null ? ' The min deposit applies to every tier.' : ''}`}
        fields={deposit == null && !list.form.row ? FIRST_LIMIT_FIELDS : LIMIT_FIELDS}
        validate={validateLimit} submit={submitLimit}
        onClose={list.closeForm} onSaved={() => { list.closeForm(); list.load(); }}
      />
      <RecordFormDialog
        open={editingDeposit} initial={depositRow} title="min deposit"
        description={`In ${currency}. Players must have deposited at least this much to get any tier's bonus; it applies to every tier.`}
        fields={DEPOSIT_FIELDS} validate={validateDeposit} submit={submitDeposit}
        onClose={() => setEditingDeposit(false)} onSaved={() => { setEditingDeposit(false); list.load(); }}
      />
      <DeleteDialog
        open={Boolean(d)} title="Delete loss tier?"
        description={d ? `The ${d.bonus_type} tier from ${d.loss_amount_from} ${currency}; the tier below it will reach up to the next one.` : ''}
        onConfirm={() => crmCampaignsAPI.deleteFridayLoyaltyLimit(d.loss_amount_from)}
        onClose={() => list.setDeleting(null)}
        onDeleted={() => { list.setDeleting(null); list.load(); }}
      />
    </>
  );
}

// --- max amounts ---

// The currency is the row's identity and stays read-only when editing.
const MAX_AMOUNT_FIELDS = [
  { key: 'currency', label: 'Currency', placeholder: 'e.g. EUR', lockedOnEdit: true },
  { key: 'amount', label: 'Max amount', inputMode: 'decimal' },
];

const validateMaxAmount = (v) => {
  if (!isValidCurrency(v.currency || '')) return 'Currency is required: up to 10 characters, no spaces.';
  if (!isValidAmount(v.amount || '') || Number(v.amount) <= 0) return 'Max amount must be a positive number.';
  return '';
};

const submitMaxAmount = (v, initial) => (initial
  ? crmCampaignsAPI.updateFridayLoyaltyMaxAmount(initial.currency, { amount: v.amount.trim() })
  : crmCampaignsAPI.createFridayLoyaltyMaxAmount({ currency: v.currency.trim(), amount: v.amount.trim() }));

const MAX_AMOUNT_COLUMNS = [
  { label: 'Currency', className: 'font-bold', render: (r) => r.currency },
  { label: 'Max amount', className: 'text-right', render: (r) => r.amount },
];

function MaxAmountsSection() {
  const list = useEditableList(crmCampaignsAPI.listFridayLoyaltyMaxAmounts, 'max_amounts');
  const d = list.deleting;
  return (
    <>
      <EditableTable
        defaultCollapsed title="Max cashback amounts" addLabel="Add currency" emptyText="No max amounts yet."
        loading={list.loading} error={list.error} rows={list.rows} columns={MAX_AMOUNT_COLUMNS}
        rowKey={(r) => r.currency}
        onAdd={list.openAdd} onEdit={list.openEdit} onDelete={list.setDeleting}
      />
      <RecordFormDialog
        open={list.form.open} initial={list.form.row} title="max amount" submitLabel="Add max amount"
        description="A currency can have one max amount."
        fields={MAX_AMOUNT_FIELDS} validate={validateMaxAmount} submit={submitMaxAmount}
        onClose={list.closeForm} onSaved={() => { list.closeForm(); list.load(); }}
      />
      <DeleteDialog
        open={Boolean(d)} title="Delete max amount?"
        description={d ? `The ${d.currency} max amount.` : ''}
        onConfirm={() => crmCampaignsAPI.deleteFridayLoyaltyMaxAmount(d.currency)}
        onClose={() => list.setDeleting(null)}
        onDeleted={() => { list.setDeleting(null); list.load(); }}
      />
    </>
  );
}

// --- tag values ---

// The tag name is the row's identity and stays read-only when editing.
const TAG_FIELDS = [
  { key: 'tag_name', label: 'Tag', lockedOnEdit: true },
  { key: 'wagering', label: 'Wagering', inputMode: 'numeric' },
  { key: 'crypto_cashback_percentage', label: 'Crypto cashback %', inputMode: 'decimal' },
  { key: 'fiat_cashback_percentage', label: 'Fiat cashback %', inputMode: 'decimal' },
  { key: 'cio_email_template', label: 'Customer.io email template' },
];

const isPercentage = (s) => isValidAmount(s || '') && Number(s) <= 100;

const validateTag = (v) => {
  if (chars(v.tag_name || '') === 0 || chars(v.tag_name) > 100) return 'Tag is required, up to 100 characters.';
  if (!isPercentage(v.crypto_cashback_percentage) || !isPercentage(v.fiat_cashback_percentage)) return 'Cashback percentages must be from 0 to 100.';
  if (!/^\d+$/.test((v.wagering || '').trim()) || Number(v.wagering) > MAX_WAGERING) return `Wagering must be a whole number from 0 to ${MAX_WAGERING}.`;
  if (chars(v.cio_email_template || '') === 0 || chars(v.cio_email_template) > 50) return 'Email template is required, up to 50 characters.';
  return '';
};

const submitTag = (v, initial) => {
  const body = {
    crypto_cashback_percentage: v.crypto_cashback_percentage.trim(),
    fiat_cashback_percentage: v.fiat_cashback_percentage.trim(),
    wagering: Number(v.wagering),
    cio_email_template: v.cio_email_template.trim(),
  };
  return initial
    ? crmCampaignsAPI.updateFridayLoyaltyTagValue(initial.tag_name, body)
    : crmCampaignsAPI.createFridayLoyaltyTagValue({ ...body, tag_name: v.tag_name.trim() });
};

const TAG_COLUMNS = [
  { label: 'Tag', className: 'font-bold', render: (r) => r.tag_name },
  { label: 'Crypto cashback %', className: 'text-right', render: (r) => r.crypto_cashback_percentage },
  { label: 'Fiat cashback %', className: 'text-right', render: (r) => r.fiat_cashback_percentage },
  { label: 'Wagering', className: 'text-right', render: (r) => r.wagering },
  { label: 'Email template', render: (r) => r.cio_email_template },
];

function TagValuesSection() {
  const list = useEditableList(crmCampaignsAPI.listFridayLoyaltyTagValues, 'tag_values');
  const d = list.deleting;
  return (
    <>
      <EditableTable
        defaultCollapsed title="Tag values" addLabel="Add tag" emptyText="No tag values yet."
        loading={list.loading} error={list.error} rows={list.rows} columns={TAG_COLUMNS}
        rowKey={(r) => r.tag_name}
        onAdd={list.openAdd} onEdit={list.openEdit} onDelete={list.setDeleting}
      />
      <RecordFormDialog
        open={list.form.open} initial={list.form.row} title="tag values" submitLabel="Add tag"
        description="A tag can have one set of values."
        fields={TAG_FIELDS} validate={validateTag} submit={submitTag}
        onClose={list.closeForm} onSaved={() => { list.closeForm(); list.load(); }}
      />
      <DeleteDialog
        open={Boolean(d)} title="Delete tag values?"
        description={d ? `The values for tag ${d.tag_name}.` : ''}
        onConfirm={() => crmCampaignsAPI.deleteFridayLoyaltyTagValue(d.tag_name)}
        onClose={() => list.setDeleting(null)}
        onDeleted={() => { list.setDeleting(null); list.load(); }}
      />
    </>
  );
}

const FridayLoyaltyPanel = () => (
  <div className="space-y-6">
    <SingleGamesSection
      load={crmCampaignsAPI.getFridayLoyaltyGames} save={crmCampaignsAPI.setFridayLoyaltyGames} />
    <MaxAmountsSection />
    <LimitsSection />
    <TagValuesSection />
  </div>
);

export default FridayLoyaltyPanel;
